import $axios from "../../api";
import FileSaver from "file-saver";

// MODULE NAME
const module = "website/loyalty-redemption";

/**
 * P26_0034 — antrean penukaran hadiah loyalty (panel admin).
 *
 * Store ini TIDAK punya action `create`. Penukaran selalu dibuat member lewat
 * endpoint redeem nfapiv2, yang sekaligus memotong saldonya. Kalau admin bisa
 * membuat penukaran dari sini, akan ada baris penukaran tanpa baris debit
 * pasangannya — dan saldo member jadi lebih besar daripada hadiah yang sudah
 * ia ambil, tanpa error apa pun.
 */

const EMPTY_LIST = { data: [], total: 0, per_page: 20, from: 0, to: 0, current_page: 1 };

/**
 * Semua kemampuan dianggap TIDAK ADA sampai server bilang sebaliknya.
 *
 * Default-nya sengaja `false`, bukan `true`: kalau muatan gagal, layar akan
 * menyembunyikan field yang belum tentu bisa disimpan — bukan menampilkannya
 * dan membiarkan admin mengetik angka yang hilang tanpa peringatan.
 */
const EMPTY_CAPABILITIES = {
    shipping_cost: false,
    admin_note: false,
    reward_sku: false,
    reward_weight: false,
    log_table: false,
};

const state = () => ({
    isLoading: false,
    isErrors: "",
    page: 1,
    isSuccess: false,
    list: EMPTY_LIST,
    summary: { 1: 0, 2: 0, 3: 0, 4: 0 },
    statusLabels: {},
    capabilities: { ...EMPTY_CAPABILITIES },
    history: [],
    historySummary: null,
    historyLoading: false,
    logs: [],
    logLoading: false,
});

const mutations = {
    SET_LOADING(state, payload) {
        state.isLoading = payload;
    },
    ASSIGN_ERRORS(state, payload) {
        state.isErrors = payload;
    },
    SET_PAGE(state, payload) {
        state.page = payload;
    },
    SET_IS_SUCCESS(state, payload) {
        state.isSuccess = payload;
    },
    SET_LIST(state, payload) {
        // Jatuh ke bentuk paginator kosong, bukan null: halaman merender
        // `list.data` langsung dan memakai `v-if="!list"` untuk layar loading,
        // jadi null akan menahan halaman di loading selamanya.
        state.list = payload || EMPTY_LIST;
    },
    SET_SUMMARY(state, payload) {
        state.summary = { 1: 0, 2: 0, 3: 0, 4: 0, ...(payload || {}) };
    },
    SET_STATUS_LABELS(state, payload) {
        state.statusLabels = payload || {};
    },
    SET_CAPABILITIES(state, payload) {
        state.capabilities = { ...EMPTY_CAPABILITIES, ...(payload || {}) };
    },
    SET_HISTORY(state, payload) {
        state.history = payload || [];
    },
    SET_HISTORY_SUMMARY(state, payload) {
        state.historySummary = payload || null;
    },
    SET_HISTORY_LOADING(state, payload) {
        state.historyLoading = payload;
    },
    SET_LOGS(state, payload) {
        state.logs = payload || [];
    },
    SET_LOG_LOADING(state, payload) {
        state.logLoading = payload;
    },
};

const actions = {
    getList({ commit, state, rootState, dispatch }, payload) {
        commit("SET_LOADING", true);
        return new Promise((resolve) => {
            $axios
                .get(`${module}/list?page=${state.page}&view=${rootState.Main.view}${payload || ""}`)
                .then((res) => {
                    commit("SET_LIST", res.data.data);
                    commit("SET_SUMMARY", res.data.summary);
                    commit("SET_STATUS_LABELS", res.data.status_labels);
                    commit("SET_CAPABILITIES", res.data.capabilities);
                    commit("SET_LOADING", false);
                    resolve(res);
                })
                .catch((error) => {
                    commit("SET_LOADING", false);
                    dispatch("Main/globalSweetAlert", error.response, { root: true });
                });
        });
    },

    confirm({ dispatch }, payload) {
        return dispatch("_post", { path: "confirm", payload });
    },

    ship({ dispatch }, payload) {
        return dispatch("_post", { path: "ship", payload });
    },

    /**
     * Penolakan bukan sekadar menggeser status — server ikut menulis baris
     * ledger kredit tipe 3 untuk mengembalikan saldo member. Karena itu galatnya
     * TIDAK boleh ditelan: kalau penulisan ledger gagal, penolakannya
     * di-rollback dan admin harus tahu bahwa saldo belum kembali.
     */
    reject({ dispatch }, payload) {
        return dispatch("_post", { path: "reject", payload });
    },

    saveNote({ dispatch }, payload) {
        return dispatch("_post", { path: "note", payload });
    },

    _post({ commit, dispatch }, { path, payload }) {
        commit("SET_LOADING", true);
        commit("SET_IS_SUCCESS", false);
        commit("ASSIGN_ERRORS", "");
        return new Promise((resolve) => {
            $axios
                .post(`${module}/${path}`, payload)
                .then((res) => {
                    commit("SET_IS_SUCCESS", true);
                    commit("SET_LOADING", false);
                    resolve(res);
                })
                .catch((error) => {
                    commit("ASSIGN_ERRORS", (error.response && error.response.data && error.response.data.errors) || "");
                    commit("SET_LOADING", false);
                    dispatch("Main/globalSweetAlert", error.response, { root: true });
                    resolve(error.response);
                });
        });
    },

    /** Riwayat Akumulasi Belanja — saldo yang dilihat member. */
    getHistory({ commit, dispatch }, memberId) {
        commit("SET_HISTORY_LOADING", true);
        commit("SET_HISTORY", []);
        commit("SET_HISTORY_SUMMARY", null);
        return new Promise((resolve) => {
            $axios
                .get(`${module}/history?member_id=${memberId}`)
                .then((res) => {
                    commit("SET_HISTORY", res.data.data);
                    commit("SET_HISTORY_SUMMARY", res.data.summary);
                    commit("SET_HISTORY_LOADING", false);
                    resolve(res);
                })
                .catch((error) => {
                    commit("SET_HISTORY_LOADING", false);
                    dispatch("Main/globalSweetAlert", error.response, { root: true });
                });
        });
    },

    /** Log Penukaran. */
    getLog({ commit, dispatch }, id) {
        commit("SET_LOG_LOADING", true);
        commit("SET_LOGS", []);
        return new Promise((resolve) => {
            $axios
                .get(`${module}/log?id=${id}`)
                .then((res) => {
                    commit("SET_LOGS", res.data.data);
                    commit("SET_LOG_LOADING", false);
                    resolve(res);
                })
                .catch((error) => {
                    commit("SET_LOG_LOADING", false);
                    dispatch("Main/globalSweetAlert", error.response, { root: true });
                });
        });
    },

    /**
     * Label AWB thermal satu penukaran, dibuka di tab baru.
     *
     * Tab dibuka SEBELUM request, bukan sesudahnya: window.open di dalam
     * .then() sudah di luar gestur klik dan diblokir popup blocker. Pola yang
     * sama dengan printAWB di stores/website/Orders.js.
     */
    printAwb({ commit }, id) {
        commit("SET_LOADING", true);
        const newTab = window.open("", "_blank");
        if (newTab) {
            newTab.document.write(
                '<p style="font-family:sans-serif;text-align:center;margin-top:50px">Sedang memuat AWB...</p>'
            );
        }

        return $axios
            .get(`${module}/print-awb/${id}`, { responseType: "blob" })
            .then((res) => {
                const fileURL = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                if (newTab) newTab.location.href = fileURL;
            })
            .catch(async (err) => {
                // responseType blob berlaku juga untuk error: pesan controller
                // ("AWB hanya bisa dicetak ...", 403 akses) ada di dalam Blob.
                let message = err.message;
                try {
                    const body = err?.response?.data;
                    const text = body instanceof Blob ? await body.text() : null;
                    message = (text ? JSON.parse(text).message : body?.message) || message;
                } catch (e) {
                    // Body bukan JSON -> pakai err.message.
                }
                if (newTab) {
                    newTab.document.body.innerHTML = "";
                    const h = newTab.document.createElement("h3");
                    h.style.color = "#d00";
                    h.textContent = "Gagal memuat AWB";
                    const pre = newTab.document.createElement("pre");
                    pre.textContent = message;
                    newTab.document.body.append(h, pre);
                }
            })
            .finally(() => {
                commit("SET_LOADING", false);
            });
    },

    exportList({ commit, dispatch }, payload) {
        commit("SET_LOADING", true);
        return new Promise((resolve) => {
            $axios
                .get(`${module}/export?${payload || ""}`, { responseType: "blob" })
                .then((res) => {
                    const d = new Date();
                    const yymmdd =
                        String(d.getFullYear()).slice(-2) +
                        String(d.getMonth() + 1).padStart(2, "0") +
                        String(d.getDate()).padStart(2, "0");
                    FileSaver.saveAs(res.data, `loyalty-redemption-${yymmdd}.xlsx`);
                    commit("SET_LOADING", false);
                    resolve(res);
                })
                .catch((error) => {
                    commit("SET_LOADING", false);
                    dispatch("Main/globalSweetAlert", error.response, { root: true });
                });
        });
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
